import colors from 'lib/constants/resourceColors';
import Privileges from '../lib/constants/privileges';
import api from 'api';
import invoiceBadgeStyle from 'lib/helpers/invoiceBadgeStyle';
import AddPaymentModal from 'components/Form/AddPaymentModal';

const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

// Shared with the per-type Invoices sub-views (resources/invoiceTypeViews.js) so
// Project/Contract/Service invoice lists render the exact same columns & fields.
export const invoiceSearchFields = ['id', 'invoice_number','description', 'grand_total','voided', 'date', 'customer_name', 'site_name', 'project_description', 'salesperson_name', 'salesperson_avatar', 'created_at','updated_at', 'open_balance', 'invoice_status'];

export const invoiceSearchColumns = [
    {heading: 'Invoice Date', cellWidth: '100px', dateFormat: 'MMM DD, YYYY', type:'date', accessor: 'date'},
    {heading: 'Invoice #', accessor: 'invoice_number', cellWidth: '110px', style: 'bold'},
    {heading: 'Project', accessor: 'project_description'},
    {heading: 'Customer', accessor: 'customer_name'},
    {heading: 'Site', accessor: 'site_name'},
    {type: 'currency', heading: 'Open Balance', accessor: 'open_balance'},
    {type: 'currency', accessor: 'grand_total', heading: 'Invoice Total'},
    {heading: 'Salesperson', accessor: 'salesperson_name'},
    {heading: 'Status', type: 'badge', accessor: 'invoice_status', badgePropsFunc: invoiceBadgeStyle},
];

// The three legacy accounting-report download buttons that lived on the old
// /invoices screen. Shared with the per-type Invoices sub-views
// (resources/invoiceTypeViews.js). Called as a factory: pass an invoice type
// (P/C/S) to scope every report to that type; call with no arg on the old
// global /invoices screen to keep them across all types. All three exclude
// voided invoices server-side regardless of type.
export const invoiceReportLinks = (type) => [
    {downloadURL: (search_state) => {
        let queries = [];
        if(search_state.search_by_date) {
            if(search_state.start_date) {
                queries.push(`start_date=${search_state.start_date}`);
            }
            if(search_state.end_date) {
                queries.push(`end_date=${search_state.end_date}`);
            }
            if(search_state.selected_date_range_type) {
                queries.push(`date_range_type=${search_state.selected_date_range_type}`);
            }
        }
        if(type) queries.push(`type=${type}`);

        return `/files/invoice_received_payments${queries.length ? `?${queries.join('&')}` : ''}`;
    },
        title: 'Download All Received Payments',
        requiredPrivileges: [Privileges.FILE_READ_INVOICE_RECEIVED_PAYMENTS]
    },
    {downloadURL: (search_state) => {
        let queries = [];
        if(search_state.search_by_date) {
            if(search_state.selected_date_range_type) {
                queries.push(`date_range_type=${search_state.selected_date_range_type}`);
            }
        }
        if(type) queries.push(`type=${type}`);

        return `/files/sales_tax_report${queries.length ? `?${queries.join('&')}` : ''}`;
    },
        title: 'Download Sales Tax Report',
        requiredPrivileges: [Privileges.FILE_READ_SALES_TAX_REPORT]
    },
    {downloadURL: (search_state) => {
        let queries = [];
        if(type) queries.push(`type=${type}`);
        return `/files/sales_tax_error_report${queries.length ? `?${queries.join('&')}` : ''}`;
    },
        title: 'Download Sales Tax Error Report',
        requiredPrivileges: [Privileges.FILE_READ_SALES_TAX_ERROR_REPORT]
    },
];

export default {
    defaults: {
        name: 'Invoice',
        color: colors.invoices,
        formFields: [
            {key:'invoice_number', static: true, label: 'Invoice #',
                computeValue: (v) => v.type ? `${v.type}-${v.invoice_number}` : v.invoice_number},
            {key:'payment_request_number',
                static: true,
                placeholder: 'None',
                disableRender: (v) => isBlank(v && v.payment_request_number),
                link: {url:'/payment_requests/:payment_request_id', privileges: Privileges.READ_PAYMENT_REQUESTS}},
            {label: 'PO Number', key:'po_number'},
            {key: 'project_description',
                readOnly: true,
                static: true,
                label: 'Project',
                disableRender: (v) => isBlank(v && v.project_description),
                link: {url:'/projects/:project_id', privileges: Privileges.READ_PROJECTS}},
            {key: 'customer_name',
                readOnly: true,
                static: true,
                label: 'Customer',
                link: {url:'/customers/:customer_id', privileges: Privileges.READ_CUSTOMERS}},
            {key: 'site_name',
                readOnly: true,
                static: true,
                label: 'Site',
                link: {url:'/sites/:site_id', privileges: Privileges.READ_SITES}},
            {key: 'salesperson_name',
                readOnly: true,
                static: true,
                label: 'Salesperson',
                disableRender: (v) => isBlank(v && v.salesperson_name),
                link: {url:'/users/:salesperson_id', privileges: Privileges.READ_USERS}},
            '/date(Date)',
            {key: 'tax_region',
                //showInCreateMode: false,
                type: 'options',
                defaultValue: 'miami-dade',
                options: [
                    {value: 'miami-dade', label: 'Miami Dade'},
                    {value: 'broward', label: 'Broward'},
                    {value: 'exempt', label: 'Tax Exempt'},
                    {value: 'notax', label: 'No Tax'},
                ],
            },
            {static: true, key: 'subtotal', type: 'currency'},
            {static: true, key: 'tax_total', label: 'Tax', type: 'currency'},
            {static: true, key: 'grand_total', label: 'Invoice Total', type: 'currency'},
            {static: true, key: 'total_paid', type: 'currency'},
            {static: true, key: 'total_bad_debt', type: 'currency'},
            {static: true, key: 'open_balance', type: 'currency'},
            {static: true, key: 'invoice_status', label: 'Status'},
            '_payment_term{COD|COD,DUE_ON_RECEIPT|Due On Receipt,NET30|Net 30,NET60|Net 60,NET90|Net 90,PROJECT|Project}',
            {key: 'parts',
                type: 'references',
                heading: 'Items',
                createLabel: 'Add Item',
                reloadPageOnCreate: true,
                dataTransform: 'items',
                dataUrl: `/invoices/:id?fields=items`,
                createUrl: (values) => { return `/invoices/${values.id}/items` },
                deleteUrl: (values) => { return `/invoices/${values.id}/items/:id` },
                editorActions: [ 
                    { label: 'Set Unit Prices to Zero', 
                       icon: 'minus-circle', 
                    method: async ({values, parameters, reload, loading, success, error}) => {
                            let invoice_id = values.id;

                            loading('Updating');
                            let result;
                            try {
                                result = await api.rpc('zeroInvoiceItemUnitPrices', {invoice_id})
                            } catch (e) {
                                throw new Error(e);
                            }
                            
                            if(result.success) {
                                success({
                                    title: 'Success', 
                                    content: 'Unit Prices have been cleared.',
                                    okText : 'Ok',
                                });
                            } else {
                                error({
                                    title: 'Error', 
                                    content: `Couldn't clear unit prices.`,
                                    okText : 'Ok',
                                });
                            }

                            reload(true);
                        }
                    }
                ],
                formTemplateSearch: {
                    displayKey: 'product_number',
                    placeholder: 'Search Product Number',
                    suggestFormat: ({product_number, description, unit_price}) => { return {
                        value: {
                            product_number,
                            description,
                            unit_price
                        },
                        text: product_number
                    } },
                    suggestUrl: '/products', 
                    suggestFields: ['id', 'product_number', 'product_number', 'description', 'unit_price'],
                    fieldMappings: {
                        'product_number': 'product_number',
                        'description': 'description',
                        'unit_price': 'price',
                    }
                },
                formFields: [
                    {key: 'product_number' },
                    {key: 'description', type: 'longtext'},
                    {key: 'price', 
                        type: 'currency', 
                        label: 'Unit Price',
                        inputRef: 'price',
                    },
                    {key: 'quantity', type: 'number'},
                    {key: 'taxable', type: 'boolean', defaultValue: true},
                ],
                tableProps: { 
                    rowURL: '/invoice_items/:id',
                    columns: [
                    {heading: 'Row', accessor:'position', type:'number', cellWidth: '10px'},
                    {accessor: 'product_number', cellWidth: '250px'},
                    {accessor: 'description'},
                    {accessor: 'price',  type:'currency', heading: 'Unit Price', cellWidth: '150px'},
                    {accessor:'quantity', type:'number', cellWidth: '50px'},
                    {accessor: 'taxable', type: 'boolean', cellWidth: '25px'},
                ] },
            },
            {key: 'payments',
                type: 'references',
                heading: 'Payments',
                createLabel: 'Add a Payment',
                createPrivileges: [Privileges.CREATE_INVOICE_PAYMENTS],
                createUrl: '/invoice_payments',
                readPrivileges: [Privileges.READ_INVOICE_PAYMENTS],
                reloadPageOnCreate: true,
                // Display-layer filter: hide voided (application-level ip.voided)
                // invoice payments from this panel. Rows remain in the DB / view
                // (append-only audit trail) — this only drops them from the UI list.
                dataTransform: (data) => (data.payments || []).filter((p) => !p.voided),
                dataUrl: `/invoices/:id?fields=payments`,
                // createUrl: (values) => { return `/invoice_payments` },
                deleteUrl: (values) => { return `/invoice_payments/:id` },
                injectedValues: (values) => { return {invoice_id: values.id }},
                //custom create UI (open-transaction picker, split Payment Amount / Amount to Apply,
                //payment-type-driven fields); falls back to formFields below if unset.
                createComponent: AddPaymentModal,
                formFields: [
                    {key: 'amount',
                        type: 'currency',
                        required: true,
                    },
                    {key: 'check_number'},
                    {label: 'Last 4 Credit Card Digits', key: 'last_digits'},
                    {key: 'memo'},
                    {key: 'file', type: 'file', label: 'Attachment', urlKey: 'source_file_url'},
                ],
                tableProps: {
                    rowURL: '/invoice_payments/:id',
                    columns: [
                    {heading: 'Payment Date', accessor: 'payment_date', type: 'date', cellWidth: '120px'},
                    {accessor: 'transaction_number', cellWidth: '150px'},
                    {accessor: 'amount', type: 'currency', cellWidth: '150px'},
                    {accessor: 'memo'},
                    {heading: 'Attachment', type: 'file', key: 'file_url', label: 'View Attachment'}
                ] },
            },


        ],
    },

    view: {
        formActions: [
            {   label: 'Print Invoice',
                icon: 'printer-o',
                method: async ({values, history, inputValues}) => {
                    console.log("Got values ", values, inputValues);
                    let pdfData = {
                        proforma: false,
                        type: values.type,
                        po_number: values.po_number,
                        salesperson: values.salesperson_name,
                        project_description: values.project_description,
                        bill_name: values.customer_name,
                        bill_address: values.customer_address,
                        bill_address2: values.customer_address2,
                        bill_city: values.customer_city,
                        bill_state: values.customer_state,
                        bill_zipcode: values.customer_zipcode,
                        ship_name: values.site_name,
                        ship_address: values.site_address,
                        ship_address2: values.site_address2,
                        ship_city: values.site_city,
                        ship_state: values.site_state,
                        ship_zipcode: values.site_zipcode,
                        ship_label: 'Site Address:',
                        payment_term: values.payment_term,
                        property_name: values.site_name,
                        shorten_descriptions: values.shorten_descriptions,
                        show_unit_prices: false,
                        payment_request_items: values.items,
                        invoice_number: values.type ? `${values.type}-${values.invoice_number}` : values.invoice_number,
                        amount_due: values.grand_total,
                        payments_made: values.total_paid,
                        balance_due: values.open_balance,
                        parts_price_subtotal: values.subtotal,
                        services_price_subtotal: 0,
                        tax_due: values.tax_total,
                        freight: 0,
                        created_at: values.created_at,
                        date: values.date
                    };
                        
                    const filename = 'payment_request'

                    let response;
                    try {
                        response = await api.create('/pdfs/payment_requests', {pdfData, filename});
                        console.log("Got response ", response);
                        window.open(response.body.url, '_blank');
                    } catch (e) {
                        console.log(`Got error ${e}`);
                        throw new Error(e);
                    }

                }
            },
            {   label: 'Email Invoice',
                icon: 'mail',
                // Greyed out unless the customer has an invoice email AND this invoice has not
                // been emailed yet. invoice_emailed is (invoices.emailed_at IS NOT NULL), surfaced
                // by invoices_extended (migration 1785300260) — the one-send lock the backend
                // enforces atomically. Both gates are re-checked server-side in emailInvoice.
                validate: ({values}) =>
                    !isBlank(values && values.customer_invoice_email) && !(values && values.invoice_emailed),
                confirm: {
                    title: 'Email Invoice',
                    getContent: (values) => {
                        const emails = (values.customer_invoice_email || '')
                            .split(',').map((s) => s.trim()).filter(Boolean);
                        const to = emails[0];
                        const cc = emails.slice(1);
                        return `Email this invoice PDF to ${to}`
                             + (cc.length ? ` (cc: ${cc.join(', ')})` : '')
                             + `? An invoice can only be emailed once.`;
                    },
                    okText: 'Send Email',
                },
                method: async ({values, reload, loading, success, error}) => {
                    const invoice_id = values.id;

                    // Same PDF the Print action produces (POST returns an S3 url in prod, a
                    // /local_pdfs fallback url in dev); emailInvoice downloads it and attaches it.
                    let pdfData = {
                        proforma: false,
                        type: values.type,
                        po_number: values.po_number,
                        salesperson: values.salesperson_name,
                        project_description: values.project_description,
                        bill_name: values.customer_name,
                        bill_address: values.customer_address,
                        bill_address2: values.customer_address2,
                        bill_city: values.customer_city,
                        bill_state: values.customer_state,
                        bill_zipcode: values.customer_zipcode,
                        ship_name: values.site_name,
                        ship_address: values.site_address,
                        ship_address2: values.site_address2,
                        ship_city: values.site_city,
                        ship_state: values.site_state,
                        ship_zipcode: values.site_zipcode,
                        ship_label: 'Site Address:',
                        payment_term: values.payment_term,
                        property_name: values.site_name,
                        shorten_descriptions: values.shorten_descriptions,
                        show_unit_prices: false,
                        payment_request_items: values.items,
                        invoice_number: values.type ? `${values.type}-${values.invoice_number}` : values.invoice_number,
                        amount_due: values.grand_total,
                        payments_made: values.total_paid,
                        balance_due: values.open_balance,
                        parts_price_subtotal: values.subtotal,
                        services_price_subtotal: 0,
                        tax_due: values.tax_total,
                        freight: 0,
                        created_at: values.created_at,
                        date: values.date
                    };

                    const label = values.type ? `${values.type}-${values.invoice_number}` : values.invoice_number;
                    const emails = (values.customer_invoice_email || '')
                        .split(',').map((s) => s.trim()).filter(Boolean);

                    loading('Emailing');

                    let pdf_url;
                    try {
                        const response = await api.create('/pdfs/payment_requests', {pdfData, filename: 'payment_request'});
                        pdf_url = response.body.url;
                    } catch (e) {
                        throw new Error(e);
                    }

                    let result;
                    try {
                        result = await api.rpc('emailInvoice', {invoice_id, pdf_url, filename: `Invoice_${label}`});
                    } catch (e) {
                        throw new Error(e);
                    }

                    if (result.success) {
                        success({
                            title: 'Invoice Emailed',
                            content: `Invoice ${label} was emailed to ${emails[0]}`
                                   + (emails.length > 1 ? ` (cc: ${emails.slice(1).join(', ')})` : '') + '.',
                            okText: 'Ok',
                        });
                    } else {
                        const rpcError = result.errors && result.errors.error;
                        error({
                            title: 'Could Not Email',
                            content: (rpcError && rpcError.message) || 'The invoice could not be emailed.',
                            okText: 'Ok',
                        });
                    }

                    reload(true);
                }
            },
            {   label: 'Void Invoice',
                icon: 'ban',
                // Only offer Void on a live invoice.
                visible: ({values}) => !values.voided,
                // Gated on the auto-seeded RPC_voidInvoice privilege (superuser bypasses).
                // Soft-void keeps the row and auto-reopens the payment request
                // (projects path) / lets Generate Invoice re-bill the period (contracts path).
                privileges: [Privileges.VOID_INVOICE],
                confirm: {
                    title: 'Void Invoice',
                    content: 'Void this invoice? It will be marked voided and its billing '
                           + 'period reopens for regeneration. An invoice with a payment '
                           + 'applied cannot be voided — reverse the payment first.',
                    okText: 'Void Invoice',
                    okType: 'danger',
                },
                method: async ({values, reload, loading, success, error}) => {
                    const invoice_id = values.id;

                    loading('Voiding');
                    let result;
                    try {
                        result = await api.rpc('voidInvoice', {invoice_id});
                    } catch (e) {
                        throw new Error(e);
                    }

                    if (result.success) {
                        success({
                            title: 'Invoice Voided',
                            content: 'This invoice was voided. Its billing period is reopened '
                                   + 'and can be regenerated.',
                            okText: 'Ok',
                        });
                    } else {
                        const rpcError = result.errors && result.errors.error;
                        error({
                            title: 'Could Not Void',
                            content: (rpcError && rpcError.message)
                                   || 'The invoice could not be voided.',
                            okText: 'Ok',
                        });
                    }

                    reload(true);
                }
            },
            {   label: 'Unvoid Invoice',
                icon: 'undo',
                // Only offer Unvoid on a voided invoice.
                visible: ({values}) => !!values.voided,
                // Gated on the auto-seeded RPC_unvoidInvoice privilege (superuser bypasses).
                // Restores the invoice to live and re-derives its status. The backend refuses if
                // the billing period was already re-invoiced while this sat voided (contract
                // ledger repointed, or the payment request re-invoiced past its total) — that
                // message is surfaced below rather than silently double-billing.
                privileges: [Privileges.UNVOID_INVOICE],
                confirm: {
                    title: 'Unvoid Invoice',
                    content: 'Unvoid this invoice? It becomes active again and its billing period '
                           + 'is consumed by it once more. If that period was already invoiced '
                           + 'again while this was voided, the unvoid will be refused.',
                    okText: 'Unvoid Invoice',
                    okType: 'primary',
                },
                method: async ({values, reload, loading, success, error}) => {
                    const invoice_id = values.id;

                    loading('Unvoiding');
                    let result;
                    try {
                        result = await api.rpc('unvoidInvoice', {invoice_id});
                    } catch (e) {
                        throw new Error(e);
                    }

                    if (result.success) {
                        success({
                            title: 'Invoice Unvoided',
                            content: 'This invoice is active again and its billing period is '
                                   + 'invoiced by it once more.',
                            okText: 'Ok',
                        });
                    } else {
                        const rpcError = result.errors && result.errors.error;
                        error({
                            title: 'Could Not Unvoid',
                            content: (rpcError && rpcError.message)
                                   || 'The invoice could not be unvoided.',
                            okText: 'Ok',
                        });
                    }

                    reload(true);
                }
            },
        ]
    },

    search: {
        allowDateFilter: true,
        heading: 'Invoices',
        fields: invoiceSearchFields,
        dateFieldOptions: [{label: 'Invoice Date', value: 'date', description: 'invoiced'},
                           {label: 'Created', value: 'created_at', description: 'created'}],
        filterOptions: [
            {label: 'Project Customer Invoices', filters: { voided:false, customer_has_projects: true }},
            {label: 'Non-Project Customer Invoices', filters: { voided: false, customer_has_projects: false }},
             {label: 'All', filters: { voided: false }},
             {label: 'Void', filters: { voided: true }},
        ],
        links: invoiceReportLinks(),
        searchBy: ['invoice number', 'project name', 'customer name', 'grand total'],
        tableProps: {
            rowURL: '/invoices/:id',
            columns: invoiceSearchColumns
        }
    }
}
